// src/pages/TestAPI.jsx
import React, { useState, useEffect, useRef } from 'react';
import { FiRefreshCw, FiSend } from 'react-icons/fi';
import feather from 'feather-icons';
import { useWabaAccounts, withAccount, accountLabel } from '../lib/waba';

export default function TestAPI() {
  // shared response log
  const [logEntries, setLogEntries] = useState([]);

  // template tester state
  const [categories, setCategories] = useState([]);
  const [approvedTemplates, setApprovedTemplates] = useState([]);
  const [parameters, setParameters] = useState([]);
  const [formData, setFormData] = useState({
    category: '',
    recipient_phone: '',
    template_name: '',
    language_code: 'en_US'
  });

  // media tester state
  const [mediaOptions, setMediaOptions] = useState([]);
  const [mediaFormData, setMediaFormData] = useState({
    recipient_phone_number: '',
    media_name: '',
    media_type: ''
  });

  // Outcome tracing. /api/send_message answers `success: true` the moment the
  // message is on the queue; whether Meta actually accepted it is only known
  // to the worker, which records the result in whatsapp_messages. So after a
  // queue-accepted send this page watches GET /api/logs for that row and adds
  // a second entry with the real outcome — sent with a wamid, or failed with
  // Meta's error text. Without it a rejected template (the canonical case:
  // `hello_world`, which Meta only lets a test number send) looks like a
  // success here and simply never arrives.
  const [tracing, setTracing] = useState(false);
  const traceRef = useRef(0);


  // The account every test on this page sends from, and whose approved
  // templates the dropdown offers. One selector for both panels: testing a
  // template on one account and the media send on another is not a thing
  // anyone wants, and two selectors would invite exactly that mistake.
  const {
    accounts: wabaAccounts,
    selected: wabaAccount,
    setSelected: setWabaAccount,
    loading: wabaLoading,
    error: wabaError,
  } = useWabaAccounts();

  useEffect(() => {
    feather.replace();
    loadCategories();
    loadMediaOptions();
  }, []);

  // Templates are per-account, so the list is reloaded on every switch and any
  // half-filled template selection is cleared with it.
  useEffect(() => {
    if (!wabaAccount) return;
    setFormData(f => ({ ...f, template_name: '' }));
    setParameters([]);
    loadTemplates();
  }, [wabaAccount]);

  // fetch notification categories
  async function loadCategories() {
    try {
      const res = await fetch('/api/notification-categories');
      const data = await res.json();
      if (data.success) setCategories(Object.keys(data.categories));
    } catch (e) {
      console.error(e);
    }
  }

  // fetch approved templates
  async function loadTemplates() {
    try {
      const res = await fetch(`/api${withAccount('/templates?status=APPROVED', wabaAccount)}`);
      const data = await res.json();
      if (data.success) setApprovedTemplates(data.templates);
    } catch (e) {
      console.error(e);
    }
  }

  // The newest whatsapp_messages row for this recipient on this account, or
  // null. The date window is deliberately wide: `date` is the server's
  // CURRENT_DATE and the browser may sit on the other side of midnight.
  async function latestLogRow(phone, account) {
    const day = d => d.toISOString().slice(0, 10);
    const now = new Date();
    const start = day(new Date(now.getTime() - 86400e3));
    const end = day(new Date(now.getTime() + 86400e3));
    const q = `/api/logs?start=${start}&end=${end}&phone=${encodeURIComponent(phone)}&limit=1`;
    const res = await fetch(withAccount(q, account));
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const rows = await res.json();
    return rows[0] || null;
  }

  // Poll until a row newer than `baseline` appears (the worker has acted on
  // the send) or the deadline passes. The baseline is the row that was
  // already newest before we queued, so the same phone being tested twice in
  // a row is not mistaken for an instant result.
  async function traceOutcome(phone, account, baselineId) {
    const token = ++traceRef.current;
    setTracing(true);
    const deadline = Date.now() + 45000;
    try {
      while (Date.now() < deadline) {
        await new Promise(r => setTimeout(r, 2000));
        if (traceRef.current !== token) return;
        let row;
        try {
          row = await latestLogRow(phone, account);
        } catch (err) {
          appendLog('error', { outcome: 'unknown', detail: `Could not read /api/logs: ${err.message}` });
          return;
        }
        if (!row || row.message_id === baselineId) continue;
        if (row.status === 'failed') {
          appendLog('error', {
            outcome: 'failed',
            waba_account: row.waba_account,
            template: row.template_id,
            error: row.error,
            hint: hintFor(row.error),
          });
        } else {
          appendLog('success', {
            outcome: row.status,
            waba_account: row.waba_account,
            template: row.template_id,
            message_id: row.message_id,
          });
        }
        return;
      }
      appendLog('error', {
        outcome: 'unknown',
        detail: 'No result from the worker within 45s. Check the Logs page or the worker-consumer container.',
      });
    } finally {
      if (traceRef.current === token) setTracing(false);
    }
  }

  // Plain-language follow-ups for the Meta errors this page runs into most.
  function hintFor(error) {
    const e = String(error || '');
    if (e.includes('131058')) {
      return "hello_world is Meta's sample template and can only be sent from a WABA test number. This account is a live number: pick another APPROVED template, or create one and wait for approval.";
    }
    if (e.includes('132001')) {
      return 'Template does not exist in this language on this account. Check the language code matches the approved template.';
    }
    if (e.includes('131047')) {
      return 'Outside the 24-hour customer service window; only template messages can be sent.';
    }
    if (e.includes('131026') || e.includes('131030')) {
      return 'Recipient cannot receive this message (not on WhatsApp, or not in the allowed list for a test number).';
    }
    if (e.includes('"code":190')) {
      return 'Access token rejected. Re-check the token saved for this account under Settings.';
    }
    return undefined;
  }

  // fetch media names + types
  async function loadMediaOptions() {
    try {
      const res = await fetch('/api/media/names');
      const data = await res.json();
      setMediaOptions(data);
    } catch (e) {
      console.error('Failed to load media options:', e);
    }
  }

  // template tester handlers
  function handleTemplateChange(name) {
    const tpl = approvedTemplates.find(t => t.name === name);
    setFormData(f => ({
      ...f,
      template_name: name,
      language_code: tpl?.language || f.language_code
    }));
    const bodyText = tpl?.components
      ?.find(c => c.type.toLowerCase() === 'body')
      ?.text || '';
    const keys = [...new Set(
      (bodyText.match(/\{\{(\d+)\}\}/g) || []).map(m => m.slice(2, -2))
    )];
    setParameters(keys.map(key => ({ key, value: '' })));
  }

  function updateParameter(i, value) {
    setParameters(ps => {
      const copy = [...ps];
      copy[i].value = value;
      return copy;
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();

    // Build payload for /api/send_message by default
    const defaultPayload = {
      ...formData,
      waba_account: wabaAccount,
      template_params: parameters.reduce((acc, p) => ({ ...acc, [p.key]: p.value }), {})
    };

    const baselineId = await baselineFor(formData.recipient_phone);

    try {
      // SPECIAL CASE: uv_auth_template should call /api/send_auth
      if (formData.template_name === 'uv_auth_template') {
        // Expecting exactly one BODY parameter used as the OTP code
        const codeValue = (parameters[0]?.value ?? '').toString();

        const authPayload = {
          recipient_phone: formData.recipient_phone,
          template_name: formData.template_name,
          language_code: formData.language_code,
          code: codeValue,
          button_index: 0,
          waba_account: wabaAccount
        };

        const res = await fetch('/api/send_auth', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(authPayload)
        });
        const data = await res.json();
        appendLog(data.success ? 'success' : 'error', data.success ? { queued: true, ...data } : data);
        if (data.success) traceOutcome(formData.recipient_phone, wabaAccount, baselineId);
        return;
      }

      // Default flow (unchanged): call /api/send_message
      const res = await fetch('/api/send_message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(defaultPayload)
      });
      const data = await res.json();
      appendLog(data.success ? 'success' : 'error', data.success ? { queued: true, ...data } : data);
      if (data.success) traceOutcome(formData.recipient_phone, wabaAccount, baselineId);
    } catch (err) {
      appendLog('error', err);
    }
  }

  // media tester handlers
  function handleMediaNameChange(name) {
    const opt = mediaOptions.find(m => m.name === name);
    setMediaFormData(f => ({
      ...f,
      media_name: name,
      media_type: opt?.type.toLowerCase() || ''
    }));
  }

  async function handleMediaSubmit(e) {
    e.preventDefault();
    const baselineId = await baselineFor(mediaFormData.recipient_phone_number);
    try {
      const res = await fetch('/api/send_media', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...mediaFormData, waba_account: wabaAccount })
      });
      const data = await res.json();
      appendLog(data.success ? 'success' : 'error', data.success ? { queued: true, ...data } : data);
      if (data.success) traceOutcome(mediaFormData.recipient_phone_number, wabaAccount, baselineId);
    } catch (err) {
      appendLog('error', err);
    }
  }

  // Newest existing log row for this phone, read before queueing, so the
  // trace knows which row is new. A failed read just means no baseline.
  async function baselineFor(phone) {
    try {
      const row = await latestLogRow(phone, wabaAccount);
      return row ? row.message_id : null;
    } catch {
      return null;
    }
  }

  // shared log utilities
  function appendLog(type, data) {
    setLogEntries(logs => [
      ...logs,
      {
        type,
        timestamp: new Date().toLocaleTimeString(),
        data: JSON.stringify(data, null, 2)
      }
    ]);
  }
  function clearLog() {
    traceRef.current += 1;   // abandon any poll in flight
    setTracing(false);
    setLogEntries([]);
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="w-full px-4 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
        <div className="mx-auto" style={{ maxWidth: '1800px' }}>

          <h1 className="text-2xl font-bold text-gray-900 mb-6">
            WABA API Testing Center
          </h1>

          {/* Above both panels, because it applies to both: every send on
              this page, template or media, leaves from this account. */}
          <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <label htmlFor="test-waba" className="text-sm font-medium text-gray-700 sm:w-56">
                Send from WhatsApp Account
              </label>
              <select
                id="test-waba"
                value={wabaAccount}
                disabled={wabaLoading || !wabaAccounts.length}
                onChange={e => setWabaAccount(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:ring-2 focus:ring-brand-200 disabled:bg-gray-50 sm:max-w-sm"
              >
                {wabaLoading && <option value="">Loading accounts…</option>}
                {!wabaLoading && !wabaAccounts.length && (
                  <option value="">No accounts configured</option>
                )}
                {!wabaLoading && wabaAccounts.map(a => (
                  <option key={a.name} value={a.name}>{accountLabel(a)}</option>
                ))}
              </select>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              {wabaError
                ? <span className="text-red-600">{wabaError}</span>
                : !wabaLoading && !wabaAccounts.length
                  ? 'Add one under Settings → WhatsApp Business Accounts.'
                  : 'Both testers below send from this account, and the template list shows only its approved templates.'}
            </p>
          </div>

          {/* two panels side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6 lg:gap-8 mb-6 lg:mb-8">

            {/* Notification Tester */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100">
              <div className="border-b border-gray-200 px-6 py-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  WhatsApp Notification Tester
                </h2>
              </div>
              <div className="p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Notification Category
                      </label>
                      <select
                        className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-200"
                        value={formData.category}
                        onChange={e => setFormData(f => ({ ...f, category: e.target.value }))}
                        required
                      >
                        <option value="" disabled>Select category</option>
                        {categories.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Recipient Phone
                      </label>
                      <input
                        type="text"
                        className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-200"
                        placeholder="+1234567890"
                        value={formData.recipient_phone}
                        onChange={e => setFormData(f => ({ ...f, recipient_phone: e.target.value }))}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Template Name
                      </label>
                      <select
                        className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-200"
                        value={formData.template_name}
                        onChange={e => handleTemplateChange(e.target.value)}
                        required
                      >
                        <option value="" disabled>Select template</option>
                        {approvedTemplates.map(t => (
                          <option key={t.id} value={t.name}>{t.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Language Code
                      </label>
                      <select
                        className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-200"
                        value={formData.language_code}
                        onChange={e => setFormData(f => ({ ...f, language_code: e.target.value }))}
                        required
                      >
                        <option value="en_US">English (US)</option>
                        <option value="en_GB">English (UK)</option>
                        <option value="fr">French</option>
                        <option value="de">German</option>
                        <option value="es">Spanish</option>
                        <option value="pt">Portuguese</option>
                        <option value="it">Italian</option>
                        <option value="ru">Russian</option>
                        <option value="zh_CN">Chinese (Simplified)</option>
                        <option value="zh_TW">Chinese (Traditional)</option>
                        <option value="ja">Japanese</option>
                        <option value="ko">Korean</option>
                        <option value="id">Indonesian</option>
                        <option value="ms">Malay</option>
                        <option value="th">Thai</option>
                        <option value="vi">Vietnamese</option>
                        <option value="tr">Turkish</option>
                        <option value="ar">Arabic</option>
                        <option value="hi">Hindi</option>
                        <option value="bn">Bengali</option>
                        <option value="gu">Gujarati</option>
                        <option value="kn">Kannada</option>
                        <option value="ml">Malayalam</option>
                        <option value="mr">Marathi</option>
                        <option value="pa">Punjabi</option>
                        <option value="ta">Tamil</option>
                        <option value="te">Telugu</option>
                        <option value="ur">Urdu</option>
                        {/* add others as needed */}
                      </select>
                    </div>
                  </div>

                  {parameters.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Template Parameters
                      </label>
                      <div className="space-y-3">
                        {parameters.map((p, i) => (
                          <input
                            key={i}
                            type="text"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-200"
                            placeholder={`Value for {{${p.key}}}`}
                            value={p.value}
                            onChange={e => updateParameter(i, e.target.value)}
                            required
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  <button
                    type="submit"
                    className="w-full py-3 px-6 bg-[#F96168] hover:bg-[#EA434B] text-white font-medium rounded-lg transition-colors shadow-sm"
                  >
                    <FiSend className="inline-block mr-2" /> Send Test Notification
                  </button>
                </form>
              </div>
            </div>

            {/* Media Tester */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100">
              <div className="border-b border-gray-200 px-6 py-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  WhatsApp Media Tester
                </h2>
              </div>
              <div className="p-6">
                <form onSubmit={handleMediaSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Recipient Phone
                      </label>
                      <input
                        type="text"
                        className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-200"
                        placeholder="+1234567890"
                        value={mediaFormData.recipient_phone_number}
                        onChange={e => setMediaFormData(f => ({
                          ...f,
                          recipient_phone_number: e.target.value
                        }))}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Media Name
                      </label>
                      <select
                        className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-200"
                        value={mediaFormData.media_name}
                        onChange={e => handleMediaNameChange(e.target.value)}
                        required
                      >
                        <option value="" disabled>Select media</option>
                        {mediaOptions.map(opt => (
                          <option key={opt.name} value={opt.name}>
                            {opt.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Media Type
                    </label>
                    <input
                      type="text"
                      className="mt-1 w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg"
                      value={mediaFormData.media_type}
                      disabled
                      readOnly
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full py-3 px-6 bg-[#F96168] hover:bg-[#EA434B] text-white font-medium rounded-lg transition-colors shadow-sm"
                  >
                    <FiSend className="inline-block mr-2" /> Send Test Notification
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* Response Log */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100">
            <div className="flex justify-between items-center border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">Response Log</h2>
              <button
                className="-mr-2 flex min-h-[40px] items-center rounded-lg px-2 text-gray-600 hover:bg-gray-100 hover:text-gray-800"
                onClick={clearLog}
              >
                <FiRefreshCw className="inline-block mr-1" /> Clear
              </button>
            </div>
            <div className="p-6 bg-gray-50 max-h-96 overflow-y-auto space-y-4">
              {tracing && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
                  Queued. Waiting for the worker to hand it to WhatsApp — the
                  outcome (message id, or Meta's error) will appear below.
                </div>
              )}
              {logEntries.length === 0 ? (
                <div className="text-gray-400 text-center py-8">
                  No responses yet.
                </div>
              ) : (
                logEntries.map((e, idx) => (
                  <div
                    key={idx}
                    className={`p-4 rounded-lg border-l-4 shadow-sm ${
                      e.type === 'success'
                        ? 'border-green-500 bg-white'
                        : 'border-red-500 bg-white'
                    }`}
                  >
                    <div className="flex justify-between mb-2">
                      <span
                        className={`font-medium ${
                          e.type === 'success' ? 'text-green-600' : 'text-red-600'
                        }`}
                      >
                        {e.type[0].toUpperCase() + e.type.slice(1)}
                      </span>
                      <span className="text-sm text-gray-500">{e.timestamp}</span>
                    </div>
                    <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                      {e.data}
                    </pre>
                  </div>
                ))
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
